import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Modal, Form, Input, InputNumber, Switch, Select, Space, message, Popconfirm, Tag, Collapse } from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    SearchOutlined,
    ReloadOutlined,
    StopOutlined,
    GiftOutlined
} from '@ant-design/icons';
import { $api } from 'services/service';
import { ITicketType, TicketTypeEnum, getTicketTypeText, formatCurrency } from '../ticketModels';

const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const TicketTypeManagement: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [ticketTypes, setTicketTypes] = useState<ITicketType[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [filterActive, setFilterActive] = useState<boolean | undefined>(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingType, setEditingType] = useState<ITicketType | null>(null);
    const [promotionPanelKey, setPromotionPanelKey] = useState<string | string[]>([]);

    useEffect(() => {
        loadTicketTypes();
    }, [pageIndex, pageSize]);
    useEffect(() => {
        handleSearch()
    }, [filterActive]);

    const loadTicketTypes = async () => {
        setLoading(true);
        try {
            const res: any = await $api.ticketType.getPaging({
                freeTextSearch: searchText || undefined,
                pageIndex,
                pageSize,
                isActive: filterActive
            });
            if (res.result) {
                setTicketTypes(res.result.items || []);
                setTotalRecords(res.result.totalRecord || 0);
            }
        } catch (error) {
            message.error('Không thể tải danh sách loại phiếu');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPageIndex(1);
        loadTicketTypes();
    };

    const handleReset = () => {
        setSearchText('');
        setFilterActive(true);
    };

    const handleAdd = () => {
        form.resetFields();
        setEditingType(null);
        setPromotionPanelKey([]); // Đóng panel khi thêm mới
        setModalVisible(true);
    };

    const handleEdit = (record: ITicketType) => {
        setEditingType(record);
        form.setFieldsValue({
            ...record,
            code: record.code,
            applicableRoles: record.applicableRoles || [],
        });
        // Tự động mở panel nếu có khuyến mãi
        if (record.buyQuantity && record.giftQuantity && record.buyQuantity > 0 && record.giftQuantity > 0) {
            setPromotionPanelKey(['promotion']);
        } else {
            setPromotionPanelKey([]);
        }
        setModalVisible(true);
    };

    const handleDelete = async (id: string) => {
        try {
            const res: any = await $api.ticketType.deleteTicketType(id);
            if (res.result !== undefined) {
                message.success('Vô hiệu loại phiếu thành công');
                loadTicketTypes();
            }
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Không thể vô hiệu loại phiếu');
        }
    };

    const handleSubmit = async (values: any) => {
        setLoading(true);
        try {
            const data = {
                ...values,
                id: editingType?.id || null,
                code: values.code.toUpperCase(),
            };
            
            const res: any = await $api.ticketType.save(data);
            if (res.result !== undefined) {
                message.success(editingType ? 'Cập nhật loại phiếu thành công' : 'Tạo loại phiếu thành công');
                setModalVisible(false);
                form.resetFields();
                setEditingType(null);
                loadTicketTypes();
            }
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Có lỗi xảy ra');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Mã</span>,
            dataIndex: 'code',
            key: 'code',
            align: 'center' as const,
            width: 90,
            render: (code: string) => <span className="font-mono font-bold text-xs sm:text-base">{code}</span>,
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Tên loại phiếu</span>,
            dataIndex: 'name',
            key: 'name',
            minWidth: 180,
            align: 'left' as const,
            render: (name: string, record: ITicketType) => (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <span className="font-semibold text-xs sm:text-base">{name}</span>
                    {record.buyQuantity && record.giftQuantity && record.buyQuantity > 0 && record.giftQuantity > 0 && (
                        <Tag
                            icon={<GiftOutlined />}
                            className="text-[10px] sm:text-sm font-bold px-1.5 sm:px-3 py-0.5 sm:py-1"
                            style={{
                                background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)',
                                color: 'white',
                                border: 'none',
                                boxShadow: '0 2px 8px rgba(255, 107, 107, 0.3)',
                                fontWeight: 'bold'
                            }}
                        >
                            <span className="hidden sm:inline">Mua {record.buyQuantity} tặng {record.giftQuantity}</span>
                            <span className="sm:hidden">{record.buyQuantity}+{record.giftQuantity}</span>
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Loại</span>,
            dataIndex: 'type',
            key: 'type',
            align: 'center' as const,
            width: 100,
            render: (type: TicketTypeEnum) => (
                <Tag color={type === TicketTypeEnum.OneTime ? 'blue' : 'green'} className="text-xs sm:text-base font-semibold px-1.5 sm:px-3 py-0.5 sm:py-1">
                    {getTicketTypeText(type)}
                </Tag>
            ),
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Giá</span>,
            dataIndex: 'price',
            key: 'price',
            align: 'center' as const,
            width: 100,
            render: (price: number) => (
                <span className="font-bold text-green-600 text-xs sm:text-lg">{formatCurrency(price)}</span>
            ),
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">HSD</span>,
            dataIndex: 'defaultExpireDays',
            key: 'defaultExpireDays',
            width: 80,
            align: 'center' as const,
            render: (days: number) => <span className="font-bold text-blue-600 text-xs sm:text-base">{days}d</span>,
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">TT</span>,
            dataIndex: 'isActive',
            key: 'isActive',
            width: 90,
            align: 'center' as const,
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'success' : 'default'} className="text-xs sm:text-base font-semibold px-1.5 sm:px-3 py-0.5 sm:py-1">
                    {isActive ? 'HĐ' : 'Ngưng'}
                </Tag>
            ),
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Đối tượng</span>,
            dataIndex: 'applicableRoles',
            key: 'applicableRoles',
            width: 160,
            align: 'center' as const,
            render: (applicableRoles: number[]) => {
                if (!applicableRoles || applicableRoles.length === 0) {
                    return <Tag color="default" className="text-xs sm:text-base px-1.5 sm:px-3 py-0.5 sm:py-1">Tất cả</Tag>;
                }
                const getRoleName = (role: number) => {
                    switch (role) {
                        case 20: return 'Căn teen';
                        case 21: return 'Nhà xe';
                        case 22: return 'KTX';
                        default: return '';
                    }
                };
                const getRoleColor = (role: number) => {
                    switch (role) {
                        case 20: return 'orange';
                        case 21: return 'cyan';
                        case 22: return 'purple';
                        default: return 'blue';
                    }
                };
                return (
                    <div className="flex flex-wrap gap-0.5 sm:gap-1 justify-center">
                        {applicableRoles.map(role => (
                            <Tag key={role} color={getRoleColor(role)} className="text-[10px] sm:text-sm px-1 sm:px-2 py-0 sm:py-0.5">
                                {getRoleName(role)}
                            </Tag>
                        ))}
                    </div>
                );
            },
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Thao tác</span>,
            key: 'action',
            align: 'center' as const,
            width: 120,
            fixed: 'right' as const,
            render: (record: ITicketType) => (
                <Space size="small">
                    <Button
                        type="primary"
                        className="focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                        ghost
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        <span className="hidden sm:inline">Sửa</span>
                    </Button>
                    <Popconfirm
                        className="focus:outline-none focus:shadow-none active:shadow-none"
                        title="Vô hiệu loại phiếu này?"
                        onConfirm={() => handleDelete(record.id!)}
                        okText="Xác nhận"
                        cancelText="Hủy"
                        okButtonProps={{ danger: true }}
                    >
                        <Button danger size="small" icon={<StopOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="space-y-3 sm:space-y-4">
            {/* Filter Section */}
            <Card className="shadow-md">
                <div className="space-y-3 sm:space-y-4">
                    <h3 className="text-base sm:text-lg font-semibold">Bộ lọc</h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                        <Input
                            placeholder="Mã, tên loại phiếu..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onPressEnter={handleSearch}
                            prefix={<SearchOutlined />}
                            size="middle"
                        />

                        <Select
                            placeholder="Trạng thái"
                            allowClear
                            value={filterActive}
                            onChange={setFilterActive}
                            size="middle"
                        >
                            <Option value={true}>Hoạt động</Option>
                            <Option value={false}>Ngưng hoạt động</Option>
                        </Select>

                        <div className="flex gap-2">
                            <Button
                                type="primary"
                                className="focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                                size="middle"
                            >
                                Tìm
                            </Button>
                            <Button
                                className="focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                                icon={<ReloadOutlined />}
                                onClick={handleReset}
                                size="middle"
                            >
                                <span className="hidden xs:inline">Đặt lại</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>

            <Card className="shadow-md overflow-hidden">
                <div className="flex flex-col xs:flex-row xs:justify-between xs:items-center gap-2 mb-3 sm:mb-4">
                    <h3 className="text-base sm:text-xl font-bold text-gray-800">Danh sách loại phiếu</h3>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                        size="middle"
                        className="focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                        style={{ outline: 'none' }}
                    >
                        <span className="hidden xs:inline">Thêm loại phiếu</span>
                        <span className="xs:hidden">Thêm</span>
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={ticketTypes}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 900 }}
                    size="small"
                    bordered
                    rowClassName={(record, index) =>
                        index % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-blue-50'
                    }
                    pagination={{
                        current: pageIndex,
                        pageSize: pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        size: 'small',
                        showTotal: (total) => <span className="text-xs sm:text-sm">Tổng {total}</span>,
                        onChange: (page, size) => {
                            setPageIndex(page);
                            setPageSize(size || 10);
                        },
                    }}
                />
            </Card>

            {/* Add/Edit Modal */}
            <Modal
                title={
                    <h2 className="text-xl font-bold">
                        {editingType ? 'Cập nhật loại phiếu' : 'Thêm loại phiếu mới'}
                    </h2>
                }
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                    setEditingType(null);
                }}
                footer={null}
                width={700}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        type: TicketTypeEnum.OneTime,
                        isActive: true,
                        defaultExpireDays: 30,
                        buyQuantity: 0,
                        giftQuantity: 0,
                        applicableRoles: [],
                    }}
                >
                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item
                            label="Mã loại phiếu"
                            name="code"
                            rules={[
                                { required: true, message: 'Vui lòng nhập mã' },
                                { len: 6, message: 'Mã phải có đúng 6 ký tự' },
                                { pattern: /^[A-Z0-9]+$/, message: 'Chỉ chứa chữ in hoa và số' }
                            ]}
                            tooltip="6 ký tự, tự động chuyển thành chữ in hoa"
                        >
                            <Input
                                placeholder="VD: VE0001"
                                maxLength={6}
                                onChange={(e) => {
                                    form.setFieldValue('code', e.target.value.toUpperCase());
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Loại phiếu"
                            name="type"
                            rules={[{ required: true, message: 'Vui lòng chọn loại' }]}
                        >
                            <Select>
                                <Option value={TicketTypeEnum.OneTime}>
                                    <div>
                                        <div className="font-semibold">Phiếu 1 lần</div>
                                    </div>
                                </Option>
                                <Option value={TicketTypeEnum.Monthly}>
                                    <div>
                                        <div className="font-semibold">Phiếu tháng</div>
                                    </div>
                                </Option>
                            </Select>
                        </Form.Item>
                    </div>

                    <Form.Item
                        label="Tên loại phiếu"
                        name="name"
                        rules={[{ required: true, message: 'Vui lòng nhập tên' }]}
                    >
                        <Input placeholder="VD: Phiếu tập gym 1 lần" />
                    </Form.Item>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item
                            label="Giá phiếu (VNĐ)"
                            name="price"
                            rules={[
                                { required: true, message: 'Vui lòng nhập giá' },
                                { type: 'number', min: 1000, message: 'Giá phải lớn hơn 1,000 VNĐ' }
                            ]}
                        >
                            <InputNumber
                                placeholder="50000"
                                style={{ width: '100%' }}
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value) => value?.replace(/\$\s?|(,*)/g, '') || ''}
                                min={0}
                                step={1000}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Hiệu lực (ngày)"
                            name="defaultExpireDays"
                            rules={[
                                { required: true, message: 'Vui lòng nhập số ngày' },
                                { type: 'number', min: 1, message: 'Phải lớn hơn 0' }
                            ]}
                            tooltip="Số ngày phiếu có hiệu lực kể từ ngày mua"
                        >
                            <InputNumber
                                placeholder="30"
                                style={{ width: '100%' }}
                                min={1}
                                max={365}
                            />
                        </Form.Item>
                    </div>

                    <Form.Item
                        label="Đối tượng sử dụng"
                        name="applicableRoles"
                        tooltip="Để trống nếu áp dụng cho tất cả đối tượng"
                    >
                        <Select
                            mode="multiple"
                            placeholder="Chọn đối tượng sử dụng (để trống = tất cả)"
                            allowClear
                        >
                            <Option value={20}>Căn teen</Option>
                            <Option value={21}>Nhà xe</Option>
                            <Option value={22}>Ký túc xá</Option>
                        </Select>
                    </Form.Item>

                    <Collapse activeKey={promotionPanelKey} onChange={setPromotionPanelKey} className="mt-4">
                        <Panel header="Khuyến mãi (Mua X tặng Y)" key="promotion">
                            <div className="grid grid-cols-2 gap-4">
                                <Form.Item
                                    label="Số lượng mua"
                                    name="buyQuantity"
                                    rules={[
                                        { required: true, message: 'Vui lòng nhập số lượng' },
                                        { type: 'number', min: 0, message: 'Phải lớn hơn hoặc bằng 0' }
                                    ]}
                                    tooltip="Nhập 0 nếu không có khuyến mãi"
                                >
                                    <InputNumber
                                        placeholder="0"
                                        style={{ width: '100%' }}
                                        min={0}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Số lượng tặng"
                                    name="giftQuantity"
                                    dependencies={['buyQuantity']}
                                    rules={[
                                        { required: true, message: 'Vui lòng nhập số lượng' },
                                        { type: 'number', min: 0, message: 'Phải lớn hơn hoặc bằng 0' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                const buyQuantity = getFieldValue('buyQuantity') || 0;
                                                if (!value || value === 0 || buyQuantity === 0) {
                                                    return Promise.resolve();
                                                }
                                                if (value > buyQuantity) {
                                                    return Promise.reject(new Error('Số lượng tặng không được lớn hơn số lượng mua'));
                                                }
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                    tooltip="Nhập 0 nếu không có khuyến mãi. Số lượng tặng không được lớn hơn số lượng mua"
                                >
                                    <InputNumber
                                        placeholder="0"
                                        style={{ width: '100%' }}
                                        min={0}
                                    />
                                </Form.Item>
                            </div>
                        </Panel>
                    </Collapse>

                    <Form.Item
                        label="Kích hoạt"
                        name="isActive"
                        valuePropName="checked"
                    >
                        <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>

                    <Form.Item className="mb-0">
                        <Space className="w-full justify-end">
                            <Button
                                onClick={() => {
                                    setModalVisible(false);
                                    form.resetFields();
                                    setEditingType(null);
                                }}
                                className="focus:outline-none focus:shadow-none active:shadow-none"
                                style={{ outline: 'none' }}
                            >
                                Hủy
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                className="focus:outline-none focus:shadow-none active:shadow-none"
                                style={{ outline: 'none' }}
                            >
                                {editingType ? 'Cập nhật' : 'Tạo mới'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default TicketTypeManagement;
